import { FieldPath } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { associateContact, makePointOfContact } from 'api/contacts/companies';
import {
    CreateContactParams,
    UpdateContactParams,
    createContact,
    transformContactParamsIntoRequest,
    updateContact,
} from 'api/contacts/single';
import { NewContactId } from 'assets/js/pages/contacts/ContactForm';
import { useContactCustomFields } from 'assets/js/pages/contacts/hooks/useContactCustomFields';
import { useContactFormSections } from 'assets/js/pages/contacts/hooks/useContactFormSections';
import { ContactFormValues, EmailItem, PhoneItem } from 'assets/js/pages/contacts/types';
import { transformAddressFormToRequest } from 'components/address-form/useAccountAddressForm';
import { toRequestValues } from 'components/custom-fields/helpers';
import { UseGivebutterFormReturn } from 'components/form/useGivebutterForm';
import { SelectItemWithLabel } from 'components/select';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentPossibleContact } from 'customHooks/useCurrentContact';
import { useFormServerErrors } from 'customHooks/useFormServerErrors';
import { accountQueryKeys } from 'queries/account-query-keys';
import { customFieldQueryKeys } from 'queries/custom-field-query-keys';
import { Contact, ContactEmail, ContactGender, ContactPhone } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

type TransformedItem<T> = ContactEmail &
    ContactPhone & {
        type: T;
        value: string;
        is_primary: boolean;
    };

const transformCommunicationData = <T extends EmailItem | PhoneItem>(value: T[]) => {
    return value
        .filter(item => item.value.length > 0)
        .map(item => ({
            type: item.type,
            value: item.value,
            is_primary: item.isPrimary,
        })) as TransformedItem<T['type']>[];
};

const useSaveContact = (
    methods: UseGivebutterFormReturn<ContactFormValues>,
    {
        onSuccess,
        onError,
    }: {
        onSuccess?: (data: Contact) => void;
        onError?: (error: GivebutterAxiosError) => void;
    },
) => {
    const { accountId } = useCurrentAccount();
    const contactCustomFields = useContactCustomFields();
    const { data: contact } = useCurrentPossibleContact();
    const { resetFormButton, setError, getValues } = methods;
    const isEdit = !!contact;
    const sections = useContactFormSections();
    const toast = useToast();
    const queryClient = useQueryClient();
    const { setAddressErrors } = useFormServerErrors(methods);

    const getSelectedId = (selected: SelectItemWithLabel<Contact>[]) => {
        return selected[0]?.id && selected[0].id !== NewContactId ? parseInt(selected[0].id) : null;
    };

    const getNewContact = (formValues: ContactFormValues) => {
        let newContact = null;

        const {
            type = ContactType.Individual,
            companyName,
            legacyTitle: title,
            pointOfContact,
            pointOfContactEmails,
            pointOfContactPhones,
            companyEmails,
            companyPhones,
        } = formValues;
        const engagePrefDefaults = {
            allowEngageEmail: false,
            allowEngageSms: false,
            allowEngageAddress: false,
        };

        if (
            (type === ContactType.Company && formValues.pointOfContact?.firstName) ||
            formValues.pointOfContact?.lastName
        ) {
            const emails = transformCommunicationData(pointOfContactEmails);
            const phones = transformCommunicationData(pointOfContactPhones);

            newContact = {
                firstName: pointOfContact?.firstName,
                lastName: pointOfContact?.lastName,
                emails,
                phones,
                title,
                type: ContactType.Individual,
                ...engagePrefDefaults,
            };
        } else if (type === ContactType.Individual && companyName) {
            const emails = transformCommunicationData(companyEmails);
            const phones = transformCommunicationData(companyPhones);

            newContact = {
                companyName,
                emails,
                phones,
                type: ContactType.Company,
                ...engagePrefDefaults,
            };
        }

        return newContact;
    };

    const buildParams = (formValues: ContactFormValues) => {
        const {
            firstName,
            lastName,
            middleName,
            dob,
            allowEngageSms,
            allowEngageEmail,
            allowEngageAddress,
            linkedInUrl,
            facebookUrl,
            websiteUrl,
            twitterUrl,
            companyName,
            legacyTitle: title,
            phones,
            emails,
            addresses,
            lastNameSuffix,
            firstNamePrefix,
            gender,
            note,
            tags,
            ui,
            type,
            companyEmails,
            companyPhones,
            company,
            employer,
        } = formValues;

        const engageParams = {
            allowEngageEmail,
            allowEngageSms,
            allowEngageAddress,
        };

        const socialParams = {
            websiteUrl,
            linkedInUrl,
            twitterUrl,
            facebookUrl,
        };

        const customFieldParams = {
            customFields: toRequestValues(getValues('customFields'), contactCustomFields),
        };

        const phoneParams = {
            phones: transformCommunicationData(
                type === ContactType.Individual ? phones : companyPhones,
            ),
        };
        const emailParams = {
            emails: transformCommunicationData(
                type === ContactType.Individual ? emails : companyEmails,
            ),
        };

        const individualParams = {
            firstName,
            middleName,
            lastName,
            title,
            gender: gender !== 'select' ? (gender.toLowerCase() as ContactGender) : null,
            prefix: ui?.showPrefix ? firstNamePrefix : null,
            suffix: ui?.showSuffix ? lastNameSuffix : null,
            dob: dob ?? null,
            employer,
            company,
        };

        const companyParams = {
            companyName,
        };

        const profileParams = {
            ...(type === ContactType.Individual ? individualParams : companyParams),
            note,
            type,
            tags: !isEdit ? tags.map(tag => tag.label) : null,
            addresses: !isEdit
                ? addresses
                      .filter(address => address.line1.length > 0)
                      .map(transformAddressFormToRequest)
                : undefined,
            ...engageParams,
            ...phoneParams,
            ...emailParams,
            ...customFieldParams,
            ...socialParams,
        };

        const nextUpdateParams: UpdateContactParams = [
            { condition: sections.social, params: socialParams },
            { condition: sections.engage, params: engageParams },
            { condition: sections.phones, params: phoneParams },
            { condition: sections.emails, params: emailParams },
            { condition: sections.profile, params: profileParams },
            { condition: sections.customFields, params: customFieldParams },
        ].reduce((acc, { condition, params }) => (condition ? { ...acc, ...params } : acc), {});

        return { profileParams, nextUpdateParams };
    };

    const { mutateAsync: create } = useMutation({
        mutationFn: (params: CreateContactParams) => {
            return createContact(accountId, params);
        },
        onError: () => {
            toast.error('Failed to create contact.');
        },
    });

    const associateAndMakePoint = async (companyId: number, contactId: number) => {
        await associateContact(accountId, companyId, { contact_id: contactId });
        return makePointOfContact(accountId, companyId, { contact_id: contactId });
    };

    const mutation = useMutation({
        mutationFn: async (formValues: ContactFormValues) => {
            const { profileParams, nextUpdateParams } = buildParams(formValues);

            const newContact = getNewContact(formValues) as unknown as CreateContactParams;

            const { selectedContact, selectedCompany, type } = formValues;
            const selectedContactId = getSelectedId(selectedContact);
            const selectedCompanyId = getSelectedId(selectedCompany);

            const data = (
                await (isEdit
                    ? updateContact(accountId, contact!.id, nextUpdateParams)
                    : create(profileParams))
            ).data;

            let companyId = selectedCompanyId;
            let contactId = selectedContactId;

            if (newContact) {
                const { data: newContactData } = await create(newContact);
                if (newContactData.type === ContactType.Company) {
                    companyId = newContactData.id;
                } else {
                    contactId = newContactData.id;
                }
            }

            if (type === ContactType.Company && contactId) {
                await associateAndMakePoint(data.id, contactId);
            } else if (type === ContactType.Individual && companyId) {
                await associateContact(accountId, companyId, { contact_id: data.id });
            }

            return { data };
        },
        onSuccess: async ({ data }) => {
            toast.success(
                isEdit ? 'Contact updated successfully' : 'Contact created successfully!',
            );

            await queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
                type: !isEdit ? 'all' : undefined,
            });

            await queryClient.invalidateQueries({
                queryKey: customFieldQueryKeys.customFields(accountId),
                type: 'all',
            });

            await queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });

            await queryClient.invalidateQueries({ queryKey: accountQueryKeys.tags(accountId) });

            onSuccess?.(data);
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const normalizedKey = transformServerKeyIntoFormKey(
                        key as Parameters<typeof transformServerKeyIntoFormKey>[0],
                    );

                    if (normalizedKey?.includes('addresses')) {
                        setAddressErrors(errors);
                        return;
                    }

                    if (normalizedKey) {
                        setError(normalizedKey as FieldPath<ContactFormValues>, {
                            message: errors[key][0],
                        });
                    }
                });
            }
            toast.error(
                error.response?.data.message ??
                    `Failed to ${isEdit ? 'update' : 'create'} contact. Please try again.`,
            );
            resetFormButton();
            onError?.(error);
        },
    });

    return mutation;
};

type ServerKeys = {
    [T in keyof ReturnType<typeof transformContactParamsIntoRequest>]: string;
};

const ignoreKeys: (keyof ServerKeys)[] = ['tags'];
const fieldArrayKeys: (keyof ServerKeys)[] = ['phones', 'emails', 'addresses'];
const transformServerKeyIntoFormKey = (
    serverKey: keyof Omit<
        ServerKeys,
        'tags' | 'primary_phone' | 'primary_address' | 'primary_email'
    >,
): keyof ContactFormValues | undefined => {
    if (ignoreKeys.some(key => key === serverKey)) {
        return undefined;
    }

    if (fieldArrayKeys.some(key => serverKey.includes(key))) {
        return serverKey as keyof ContactFormValues;
    }

    if (serverKey.includes('custom_fields')) {
        return serverKey.replace('custom_fields', 'customFields') as keyof ContactFormValues;
    }

    switch (serverKey) {
        case 'first_name':
            return 'firstName';
        case 'last_name':
            return 'lastName';
        case 'prefix':
            return 'firstNamePrefix';
        case 'middle_name':
            return 'middleName';
        case 'email_subscription':
            return 'allowEngageEmail';
        case 'phone_subscription':
            return 'allowEngageSms';
        case 'address_subscription':
            return 'allowEngageAddress';
        case 'suffix':
            return 'lastNameSuffix';
        case 'title':
            return 'legacyTitle';
        case 'linkedin_url':
            return 'linkedInUrl';
        case 'facebook_url':
            return 'facebookUrl';
        case 'twitter_url':
            return 'twitterUrl';
        case 'website_url':
            return 'websiteUrl';
        case 'custom_fields':
            return 'customFields';
        case 'company_name':
            return 'companyName';
        case 'dob':
        case 'gender':
        default:
            return serverKey;
    }
};

export { useSaveContact, transformCommunicationData, transformServerKeyIntoFormKey };
